import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseDateUK, formatDateUK } from "../utils/dateUtils";
import "../assets/variables.css";
import "../assets/users.css";
import "../assets/panels.css";
import Loader from "../components/Loader";
import S3ImageUpload from "../components/S3ImageUpload";
import { useAuth } from "../contexts/AuthContext";

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, updateUser, getUserRoleLevel } = useAuth();

  // Initial state matching Laravel form structure
  const [user, setUser] = useState({
    // Common fields (matching Laravel fields.blade.php)
    role_id: "",
    salutation: "",
    first_name: "",
    last_name: "",
    email: "",
    email_alternative: "",
    password: "",
    confirm_password: "",
    current_password: "",
    image: null,
    imageKey: null, // S3 key for uploaded image
    job_title: "",
    phone_number: "",
    mobile_number: "",
    address_1: "",
    address_2: "",
    address_3: "",
    town: "",
    postcode: "",
    fax_number: "",
    staff: 0,
    staff_guardian_page: 0,
    staff_property_owners: 0,
    staff_about_us_page: 0,
    can_review_certificates: 0,
    department_id: "",
    biography: "",
    color: "#000000",
    rec_notif_of_property_status: 0,
    rec_notif_of_property_id: 0,

    // Guardian fields (matching Laravel fields-guardian.blade.php)
    country_id: "",
    religion_id: "",
    hear_about_id: "",
    gender: "",
    gender_freetext: "",
    date_of_birth: "",
    national_insurance_number: "",
    criminal_record: 0,
    criminal_record_description: "",
    medical_condition: 0,
    medical_condition_description: "",
    next_of_kin: "",
    notes: "",

    // Client fields (matching Laravel fields-client-contact.blade.php)
    client_id: "", // Flattened for backend
    position: "",
    department: "",
    reports_to: "",
    description: "",
    access_to_ntq: 0,

    // Contractor fields (matching Laravel fields-contractor-contact.blade.php)
    contractor_id: "", // Flattened for backend
    contractor_position: "",
    contractor_department: "",
    contractor_reports_to: "",
    date_started_with_company: "",
  });

  const [dropdowns, setDropdowns] = useState({
    roles: [],
    nationalities: [],
    religions: [],
    clients: [],
    contractors: [],
    hearAbouts: [],
    departments: [],
    genders: [
      { id: 1, name: "Male" },
      { id: 2, name: "Female" },
      { id: 3, name: "Other" },
      { id: 4, name: "Prefer not to say" },
      { id: 5, name: "Non-binary" },
      { id: 6, name: "Let me Type*" },
    ],
  });

  // Role IDs matching database
  const ROLE_IDS = {
    GUARDIAN: 9,
    CLIENT: 8,
    CONTRACTOR: 10,
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  // Detect if user is editing their own profile (self-edit)
  const isSelfEdit = authUser && authUser.id === parseInt(id);

  useEffect(() => {
    // Fetch user data and dropdown data
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/api/users/${id}/edit`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
      fetch(`${process.env.REACT_APP_API_URL}/api/users/create-data`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
    ])
      .then((responses) => Promise.all(responses.map((res) => res.json())))
      .then(([userData, dropdownData]) => {
        // Map API response to form state structure
        const apiUser = userData.user;
        const userable = apiUser.userable || {};

        // Check if we have a contractor_id in localStorage (from contractor contacts list)
        const storedContractorId = localStorage.getItem("contractor_id");
        if (storedContractorId) {
          localStorage.removeItem("contractor_id"); // Clear it after use
          userable.contractor_id = storedContractorId;
        }

        // Get role_id from API response
        const roleId = apiUser.role_id || "";
        const roleName = dropdownData.roles.find((r) => r.id === parseInt(roleId))?.name;

        console.log("API User Data:", {
          role: apiUser.role,
          role_id: apiUser.role_id,
          role_name: roleName,
          userable: apiUser.userable,
        });

        const mappedUser = {
          ...user, // Start with initial state
          // Common fields
          role_id: roleId,
          salutation: apiUser.salutation || "",
          first_name: apiUser.first_name || "",
          last_name: apiUser.last_name || "",
          email: apiUser.email || "",
          email_alternative: apiUser.email_alternative || "",
          job_title: apiUser.job_title || "",
          phone_number: apiUser.phone_number || "",
          mobile_number: apiUser.mobile_number || "",
          address_1: apiUser.address_1 || "",
          address_2: apiUser.address_2 || "",
          address_3: apiUser.address_3 || "",
          town: apiUser.town || "",
          postcode: apiUser.postcode || "",
          fax_number: apiUser.fax_number || "",
          staff: apiUser.staff || 0,
          staff_guardian_page: apiUser.staff_guardian_page || 0,
          staff_property_owners: apiUser.staff_property_owners || 0,
          staff_about_us_page: apiUser.staff_about_us_page || 0,
          can_review_certificates: apiUser.can_review_certificates || 0,
          department_id: apiUser.department_id || "",
          biography: apiUser.biography || "",
          color: apiUser.color || "#000000",
          rec_notif_of_property_status:
            apiUser.rec_notif_of_property_status || 0,
          rec_notif_of_property_id: apiUser.rec_notif_of_property_id || 0,
          image: apiUser.image || "",

          // Guardian fields (from userable)
          country_id: userable.country_id || "",
          religion_id: userable.religion_id || "",
          hear_about_id: userable.hear_about_id || "",
          gender: apiUser.gender || userable.gender || "",
          gender_freetext: userable.gender_freetext || "",
          date_of_birth: userable.date_of_birth || "",
          national_insurance_number: userable.national_insurance_number || "",
          criminal_record: userable.criminal_record || 0,
          criminal_record_description:
            userable.criminal_record_description || "",
          medical_condition: userable.medical_condition || 0,
          medical_condition_description:
            userable.medical_condition_description || "",
          next_of_kin: userable.next_of_kin || "",
          notes: userable.notes || "",

          // Client fields (from userable)
          client_id: userable.client_id || "",
          position: userable.position || "",
          department: userable.department || "",
          reports_to: userable.reports_to || "",
          description: userable.description || "",
          access_to_ntq: userable.access_to_ntq || 0,

          // Contractor fields (from userable)
          contractor_id: userable.contractor_id || "",
          contractor_position: userable.position || "",
          contractor_department: userable.department || "",
          contractor_reports_to: userable.reports_to || "",
          date_started_with_company: formatDateToDDMMYYYY(userable.date_started_with_company) || "",
        };

        console.log("Mapped User State:", {
          role_id: mappedUser.role_id,
          isGuardian: parseInt(mappedUser.role_id) === ROLE_IDS.GUARDIAN,
          isClient: parseInt(mappedUser.role_id) === ROLE_IDS.CLIENT,
          isContractor: parseInt(mappedUser.role_id) === ROLE_IDS.CONTRACTOR,
        });

        setUser(mappedUser);
        setImagePreview(apiUser.image || null);
        setDropdowns(dropdownData);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load user data");
        setLoading(false);
      });
  }, [id]); // Only re-run when id changes

  // Format date from ISO or any format to dd/mm/yyyy
  const formatDateToDDMMYYYY = (dateValue) => {
    if (!dateValue) return "";
    
    // If already in dd/mm/yyyy format, return as is
    if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Try to parse as Date object (handles ISO strings like "2025-11-27T00:00:00.000Z")
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return ""; // Invalid date
      }
      
      // Format as dd/mm/yyyy
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Helper functions for date conversion (matching UserCreate)
  const convertToLaravelDate = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const convertToISODate = (laravelDate) => {
    if (!laravelDate) return "";
    const [day, month, year] = laravelDate.split("/");
    return `${year}-${month}-${day}`;
  };

  // Handle key press for phone number fields to prevent non-numeric input
  const handleKeyDown = (e) => {
    const { name } = e.target;
    if (name === "mobile_number" || name === "phone_number") {
      // Allow: numbers, space, hyphen, plus, parentheses, backspace, delete, tab, escape, enter, arrows
      const allowedKeys = ['0','1','2','3','4','5','6','7','8','9',' ','-','+','(',')'];
      const specialKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
        return;
      }
      
      if (!allowedKeys.includes(e.key) && !specialKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  // Handle paste event for phone number fields to clean pasted content
  const handlePaste = (e) => {
    const { name } = e.target;
    if (name === "mobile_number" || name === "phone_number") {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const cleanedText = pastedText.replace(/[^0-9\s\-+()]/g, '');
      console.log('Paste filtered:', pastedText, '->', cleanedText);
      
      setUser((prev) => ({
        ...prev,
        [name]: cleanedText,
      }));
    }
  };

  // Handle input event as a backup filter
  const handleInput = (e) => {
    const { name, value } = e.target;
    if (name === "mobile_number" || name === "phone_number") {
      const cleanedValue = value.replace(/[^0-9\s\-+()]/g, '');
      if (cleanedValue !== value) {
        console.log('Input event filtered:', value, '->', cleanedValue);
        e.target.value = cleanedValue;
        setUser((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    }
  };

  // Helper for dot notation in handleChange
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for date fields (matching UserCreate)
    if (type === "date") {
      setUser((prev) => ({
        ...prev,
        [name]: value ? convertToLaravelDate(value) : "",
      }));
    } else if (name === "mobile_number" || name === "phone_number") {
      // Allow only numbers, spaces, hyphens, plus signs, and parentheses for phone fields
      const phoneValue = value.replace(/[^0-9\s\-+()]/g, '');
      console.log('Phone input filtered:', value, '->', phoneValue);
      setUser((prev) => ({
        ...prev,
        [name]: phoneValue,
      }));
    } else {
      setUser((prev) => {
        const newUser = {
          ...prev,
          [name]: type === "checkbox" || type === "radio" ? type === "radio" ? parseInt(value) : checked : value,
        };

        // Clear gender_freetext when gender is not "Let me Type*"
        if (name === "gender" && value !== 6) {
          newUser.gender_freetext = "";
        }

        // Clear description fields when radio buttons are set to "No"
        if (name === "criminal_record" && parseInt(value) === 0) {
          newUser.criminal_record_description = "";
        }
        if (name === "medical_condition" && parseInt(value) === 0) {
          newUser.medical_condition_description = "";
        }

        return newUser;
      });
    }
  };

  const handleRadioChange = (name, value) => {
    setUser((prev) => {
      const newUser = { ...prev, [name]: value };

      // Clear description fields when radio buttons are set to "No"
      if (name === "criminal_record" && value === 0) {
        newUser.criminal_record_description = "";
      }
      if (name === "medical_condition" && value === 0) {
        newUser.medical_condition_description = "";
      }

      return newUser;
    });
  };

  // Helper function to validate hex color
  const isValidHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  // Helper function to get valid color for display
  const getValidColor = (color) => {
    if (isValidHexColor(color)) {
      return color;
    }
    // If invalid, try to fix common issues
    if (color && color.startsWith("#") && color.length === 5) {
      // Add missing character to make it 6 digits
      return color + "0";
    }
    return "#000000"; // Default fallback
  };

  const handleColorChange = (e) => {
    setUser((prev) => ({ ...prev, color: e.target.value }));
  };

  // Handle image upload from S3ImageUpload component
  const handleImageUpload = (imageKey, file) => {
    setUser((prev) => ({
      ...prev,
      image: file, // Keep file for preview
      imageKey: imageKey, // Store S3 key for backend
      imageName: file?.name || "", // Store file name for backend validation
      imageType: file?.type || "", // Store file type for backend validation
      imageSize: file?.size || 0, // Store file size for backend validation
    }));
    setError(null); // Clear any previous errors
    // Clear image validation errors when a new image is uploaded
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.image;
      return newErrors;
    });
  };

  // Conditional rendering helpers (matching Laravel UserFormController.js logic)
  const isGuardian = React.useMemo(() => parseInt(user.role_id) === ROLE_IDS.GUARDIAN, [user.role_id, ROLE_IDS.GUARDIAN]);
  const isClient = React.useMemo(() => parseInt(user.role_id) === ROLE_IDS.CLIENT, [user.role_id, ROLE_IDS.CLIENT]);
  const isContractor = React.useMemo(() => parseInt(user.role_id) === ROLE_IDS.CONTRACTOR, [user.role_id, ROLE_IDS.CONTRACTOR]);
  const isStaff = React.useMemo(() => parseInt(user.staff) === 1, [user.staff]);

  // Debug conditional rendering
  console.log("Conditional Rendering Debug:", {
    userRoleId: user.role_id,
    ROLE_IDS,
    isGuardian,
    isClient,
    isContractor,
    isStaff,
    userStaff: user.staff,
  });

  const validate = React.useCallback(() => {
    const newErrors = {};

    // Basic validation (matching Laravel CreateUserRequest)
    if (!user.first_name) newErrors.first_name = "First name is required";
    if (!user.last_name) newErrors.last_name = "Last name is required";
    if (!user.email) newErrors.email = "Email is required";
    if (!user.mobile_number)
      newErrors.mobile_number = "Mobile number is required";
    if (!user.role_id) newErrors.role_id = "Role is required";

    // Email format validation (enhanced)
    if (user.email && !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email)) {
      newErrors.email = "Please enter a valid email address (e.g., user@example.com)";
    }

    // Mobile number validation (character count and format)
    if (user.mobile_number) {
      // Remove all non-numeric characters for validation
      const cleanMobileNumber = user.mobile_number.replace(/\D/g, '');
      
      if (cleanMobileNumber.length < 10) {
        newErrors.mobile_number = "Mobile number must be at least 10 digits";
      } else if (cleanMobileNumber.length > 15) {
        newErrors.mobile_number = "Mobile number cannot exceed 15 digits";
      } else if (!/^[\d\s\-+()]+$/.test(user.mobile_number)) {
        newErrors.mobile_number = "Mobile number can only contain numbers, spaces, hyphens, plus signs, and parentheses";
      }
    }

    // Password validation for self-edits (matching Laravel editMyProfile behavior)
    if (isSelfEdit && (user.password || user.confirm_password)) {
      if (!user.current_password || user.current_password.trim() === "") {
        newErrors.current_password = "Current password is required to change your password";
      }
      if (user.password !== user.confirm_password) {
        newErrors.confirm_password = "Password and confirm password do not match";
      }
    }

    // Image validation (matching backend validation)
    if (user.image && user.image instanceof File) {
      // File type validation
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/bmp",
      ];

      if (!allowedTypes.includes(user.image.type)) {
        newErrors.image =
          "Image must be a valid image file (JPEG, PNG, GIF, WebP, or BMP)";
      }

      // File size validation (max 100MB as per backend)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (user.image.size > maxSize) {
        newErrors.image = `Image file size must not exceed ${Math.round(
          maxSize / (1024 * 1024)
        )}MB`;
      }

      // File extension validation
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
      const fileExtension = user.image.name.toLowerCase().split(".").pop();
      if (!allowedExtensions.includes(fileExtension)) {
        newErrors.image =
          "Image file extension must be one of: jpg, jpeg, png, gif, webp, bmp";
      }
    }

    // Guardian-specific validation (matching Laravel validation)
    if (isGuardian) {
      if (!user.gender) newErrors.gender = "Gender must be a valid selection";
      // Gender freetext validation (required when gender is "Let me Type*")
      if (parseInt(user.gender) === 6 && (!user.gender_freetext || user.gender_freetext.trim() === "")) {
        newErrors.gender_freetext = "Gender description is required when 'Let me Type*' is selected";
      }
      // Gender freetext length validation
      if (user.gender_freetext && user.gender_freetext.length > 25) {
        newErrors.gender_freetext = "Gender description must not exceed 25 characters";
      }
      // Alternative email validation (optional, but must be valid format if provided)
      if (user.email_alternative && !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email_alternative)) {
        newErrors.email_alternative = "Please enter a valid alternative email address";
      }
      // Check that alternative email is not the same as primary email
      if (user.email_alternative && user.email_alternative.toLowerCase() === user.email.toLowerCase()) {
        newErrors.email_alternative = "Alternative email must be different from primary email";
      }
      if (!user.date_of_birth) {
        newErrors.date_of_birth = "Date of birth is required for guardians";
      } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(user.date_of_birth)) {
        newErrors.date_of_birth = "Date of birth must be in format dd/mm/yyyy";
      }
      if (user.criminal_record === 1 && !user.criminal_record_description) {
        newErrors.criminal_record_description =
          "Criminal record description is required when criminal record is selected";
      }
      if (user.medical_condition === 1 && !user.medical_condition_description) {
        newErrors.medical_condition_description =
          "Medical condition description is required when medical condition is selected";
      }
    }

    // Client-specific validation
    if (isClient && !user.client_id) {
      newErrors.client_id = "Client is required for client contacts";
    }

    // Contractor-specific validation
    if (isContractor) {
      if (!user.contractor_id) {
        newErrors.contractor_id = "Contractor is required for contractor contacts";
      }
      if (user.date_started_with_company && !/^\d{2}\/\d{2}\/\d{4}$/.test(user.date_started_with_company)) {
        newErrors.date_started_with_company = "Date started with company must be in format dd/mm/yyyy";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [user, isGuardian, isClient, isContractor, isSelfEdit, setErrors]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);

    // Prepare data for submission - map to Node.js API structure
    const submitData = {
      // Common user fields (matching usersEntity.js)
      salutation: user.salutation || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      job_title: user.job_title || "",
      phone_number: user.phone_number || "",
      mobile_number: user.mobile_number || "",
      address_1: user.address_1 || "",
      address_2: user.address_2 || "",
      address_3: user.address_3 || "",
      town: user.town || "",
      postcode: user.postcode || "",
      fax_number: user.fax_number || "",
      staff: user.staff || 0,
      staff_guardian_page: user.staff_guardian_page || 0,
      staff_property_owners: user.staff_property_owners || 0,
      staff_about_us_page: user.staff_about_us_page || 0,
      can_review_certificates: user.can_review_certificates || 0,
      department_id: user.department_id || null,
      biography: user.biography || "",
      color: user.color || "#000000",
      rec_notif_of_property_status: user.rec_notif_of_property_status || 0,
      rec_notif_of_property_id: user.rec_notif_of_property_id || 0,

      // Image handling
      image: user.imageKey || user.image || "",
      imageName: user.imageName || "",
      imageType: user.imageType || "",
      imageSize: user.imageSize || 0,

      // Password fields (only if provided)
      ...(user.password && { password: user.password }),
      ...(user.confirm_password && { confirm_password: user.confirm_password }),
      ...(user.current_password && { current_password: user.current_password }),

      // Role-specific fields based on role_id
      ...(user.role_id && { role_id: user.role_id }),

      // Guardian fields (if role is guardian)
      ...(isGuardian && {
        email_alternative: user.email_alternative || null,
        country_id: user.country_id || null,
        religion_id: user.religion_id || null,
        hear_about_id: user.hear_about_id || null,
        gender: user.gender || null,
        gender_freetext: user.gender_freetext || null,
        date_of_birth: user.date_of_birth || null,
        national_insurance_number: user.national_insurance_number || null,
        criminal_record: user.criminal_record || 0,
        criminal_record_description: user.criminal_record_description || null,
        medical_condition: user.medical_condition || 0,
        medical_condition_description:
          user.medical_condition_description || null,
        next_of_kin: user.next_of_kin || null,
        notes: user.notes || null,
      }),

      // Client fields (if role is client)
      ...(isClient && {
        client_id: user.client_id || null,
        position: user.position || null,
        department: user.department || null,
        reports_to: user.reports_to || null,
        description: user.description || null,
        access_to_ntq: user.access_to_ntq || 0,
      }),

      // Contractor fields (if role is contractor)
      ...(isContractor && {
        contractor_id: user.contractor_id || null,
        contractor_position: user.contractor_position || null,
        contractor_department: user.contractor_department || null,
        contractor_reports_to: user.contractor_reports_to || null,
        date_started_with_company: user.date_started_with_company || null,
      }),
    };

    fetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(submitData),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then(errorData => {
            throw new Error(JSON.stringify(errorData));
          });
        }
        return res.json();
      })
      .then((data) => {
        setSuccess(true);
        setSaving(false);

        // Update AuthContext user data if this is the current user's profile
        if (authUser && authUser.id === parseInt(id)) {
          const updatedUserData = {
            ...authUser,
            ...submitData,
            // Ensure the image is updated with the new S3 key
            image: user.imageKey || user.image || authUser.image,
          };
          updateUser(updatedUserData);
        }

        // Navigate based on user role if this is the current user's profile
        setTimeout(() => {
          // Check if this is the current user editing their own profile
          if (authUser && authUser.id === parseInt(id)) {
            // Get the user's role to determine redirection
            const userRole = authUser.role || authUser.userable_type;
            
            if (userRole === 'GUARDIAN' || userRole === 'Guardian' || userRole === 'App\\Guardians\\Guardian' || userRole === 'guardians') {
              navigate('/guardian-dashboard');
            } else if (userRole === 'CLIENT' || userRole === 'Client' || userRole === 'App\\Clients\\Client' || userRole === 'clients') {
              navigate('/dashboard');
            } else {
              // For other roles (staff, admin, etc.), go to user detail page
              navigate(`/users/${id}`);
            }
          } else {
            // For editing other users, go to user detail page
            navigate(`/users/${id}`);
          }
        }, 1000);
      })
      .catch((error) => {
        console.error("❌ Error updating user:", error);
        setSaving(false);
        
        try {
          const errorData = JSON.parse(error.message);
          
          // Handle validation errors
          if (errorData.error === "VALIDATION_ERROR" && errorData.details) {
            // Display validation errors on the form
            const validationErrors = {};
            errorData.details.forEach(detail => {
              console.log("Processing validation error:", detail);
              if (detail.includes("Email already exists") || detail.includes("email")) {
                validationErrors.email = "This email address is already registered";
              } else if (detail.includes("mobile_number") || detail.includes("Mobile")) {
                validationErrors.mobile_number = detail;
              } else if (detail.includes("first_name") || detail.includes("First")) {
                validationErrors.first_name = detail;
              } else if (detail.includes("last_name") || detail.includes("Last")) {
                validationErrors.last_name = detail;
              } else if (detail.includes("role") || detail.includes("Role")) {
                validationErrors.role_id = detail;
                             } else if (detail.includes("password") || detail.includes("Password")) {
                 validationErrors.password = detail;
               } else if (detail.includes("Gender") || detail.includes("gender")) {
                 validationErrors.gender = detail;
               } else if (detail.includes("contractor") || detail.includes("Contractor")) {
                 validationErrors.contractor_id = detail;
               } else if (detail.includes("client") || detail.includes("Client")) {
                 validationErrors.client_id = detail;
               } else if (detail.includes("date_of_birth") || detail.includes("Date of birth")) {
                 validationErrors.date_of_birth = detail;
               } else if (detail.includes("date_started_with_company") || detail.includes("Date started")) {
                 validationErrors.date_started_with_company = detail;
               } else {
                 // Generic error handling - add to general error
                 setError(detail);
               }
            });
            
            console.log("Setting validation errors:", validationErrors);
            setErrors(validationErrors);
          } else if (errorData.message) {
            setError(errorData.message);
          } else {
            setError("Failed to update user");
          }
        } catch (parseError) {
          // If error parsing fails, show generic error
          setError(error.message || "Failed to update user");
        }
      });
  };

  if (loading) return <Loader />;
  // if (error) return <div>{error}</div>;
  // if (!user) return <div>User not found</div>;

  // Debug role information
  const currentRole = dropdowns.roles.find((r) => r.id === parseInt(user.role_id))?.name;
  console.log("Role Debug:", {
    user_role_id: user.role_id,
    currentRole,
    roles: dropdowns.roles,
    ROLE_IDS,
    gender: user.gender,
    gender_freetext: user.gender_freetext,
  });

  return (
    <div
      className="container js-UserFormController"
      style={{
        maxWidth: 900,
        margin: "0 auto",
        fontFamily: "var(--font-roboto), Arial, sans-serif",
        padding: 32,
      }}
    >
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        {!isSelfEdit && (
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/users')}
          >
            Users
          </span>
        )}
        {isSelfEdit && (
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
          >
            My Profile
          </span>
        )}
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">{isSelfEdit ? 'Edit my profile' : 'Edit user'}</span>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12">
          <div
            className="panel panel-default panel-form"
            style={{
              background: "white",
              borderRadius: 8,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              padding: 0,
            }}
          >
            <div className="panel-heading panel-title" style={{ margin: 0 }}>
              <h3
                style={{
                  fontWeight: 600,
                  fontFamily: "var(--font-stag), serif",
                  fontSize: "1.5rem",
                  margin: 0,
                }}
              >
                {isSelfEdit ? 'Edit my profile' : 'Edit user'}
              </h3>
            </div>

            <div className="panel-body" style={{ padding: 32 }}>
              <form
                className="form-horizontal"
                onSubmit={handleSubmit}
                encType="multipart/form-data"
                noValidate
              >
                <fieldset className="form-horizontal">
                  {/* Role - editable for Senior Mgmt / System Admin; read-only otherwise.
                      System Administrator targets are locked and System Administrator
                      is filtered from the dropdown. Self-edit of role is blocked. */}
                  {(() => {
                    const SYSTEM_ADMIN_ROLE_ID = 1;
                    const targetRoleId = parseInt(user.role_id);
                    const canEditRole =
                      typeof getUserRoleLevel === "function" &&
                      getUserRoleLevel() >= 8 &&
                      targetRoleId !== SYSTEM_ADMIN_ROLE_ID &&
                      !isSelfEdit;

                    return (
                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          htmlFor="role_id"
                          className="control-label col-sm-3 label-required"
                          style={{ textAlign: "right" }}
                        >
                          Role
                        </label>
                        <div className="col-sm-5">
                          {canEditRole ? (
                            <select
                              id="role_id"
                              name="role_id"
                              className="form-control"
                              value={user.role_id || ""}
                              onChange={(e) =>
                                setUser({ ...user, role_id: e.target.value })
                              }
                            >
                              <option value="">Please Select</option>
                              {dropdowns.roles
                                .filter((r) => r.id !== SYSTEM_ADMIN_ROLE_ID)
                                .map((r) => (
                                  <option key={r.id} value={r.id}>
                                    {r.name}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <>
                              <div className="form-control-static">
                                {dropdowns.roles.find(
                                  (r) => r.id === targetRoleId
                                )?.name || "Unknown Role"}
                              </div>
                              <input
                                type="hidden"
                                name="role_id"
                                value={user.role_id}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Common Fields - matches Laravel fields.blade.php exactly */}
                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="salutation"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Title
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="salutation"
                        name="salutation"
                        value={user.salutation}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className={`form-group mb-3${
                      errors.first_name ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="first_name"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      First Name
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="first_name"
                        name="first_name"
                        value={user.first_name}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                      {errors.first_name && (
                        <span className="help-block text-danger">
                          {errors.first_name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className={`form-group mb-3${
                      errors.last_name ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="last_name"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Last Name
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="last_name"
                        name="last_name"
                        value={user.last_name}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                      {errors.last_name && (
                        <span className="help-block text-danger">
                          {errors.last_name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className={`form-group mb-3${
                      errors.email ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="email"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Email
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="email"
                        name="email"
                        value={user.email}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="e.g., john.doe@example.com"
                        required
                        type="email"
                        readOnly={isSelfEdit}
                        style={isSelfEdit ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                      {user.email && (
                        <div className="help-block" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email) ? (
                            <span style={{ color: '#5cb85c' }}>✓ Valid email format</span>
                          ) : (
                            <span style={{ color: '#d9534f' }}>Please use a valid email format</span>
                          )}
                        </div>
                      )}
                      {errors.email && (
                        <span className="help-block text-danger">
                          {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Alternative Email - only for Guardian users */}
                  {isGuardian && (
                    <div
                      className={`form-group mb-3${
                        errors.email_alternative ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="email_alternative"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Alternative Email
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="email_alternative"
                          name="email_alternative"
                          value={user.email_alternative}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="e.g., alternate@example.com"
                          type="email"
                        />
                        {user.email_alternative && (
                          <div className="help-block" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {user.email_alternative.toLowerCase() === user.email.toLowerCase() ? (
                              <span style={{ color: '#d9534f' }}>Alternative email must be different from primary email</span>
                            ) : /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email_alternative) ? (
                              <span style={{ color: '#5cb85c' }}>✓ Valid email format</span>
                            ) : (
                              <span style={{ color: '#d9534f' }}>Please use a valid email format</span>
                            )}
                          </div>
                        )}
                        <span className="help-block" style={{ fontSize: '11px', color: '#888' }}>
                          Guardian can also login using this email address
                        </span>
                        {errors.email_alternative && (
                          <span className="help-block text-danger">
                            {errors.email_alternative}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Current Password field - only for self-edits when changing password */}
                  {isSelfEdit && (user.password || user.confirm_password) && (
                    <div
                      className={`form-group mb-3${
                        errors.current_password ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="current_password"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Current Password
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="current_password"
                          name="current_password"
                          type="password"
                          value={user.current_password}
                          onChange={handleChange}
                          className="form-control"
                          autoComplete="current-password"
                          required
                        />
                        <span className="help-block">
                          Required when changing your password
                        </span>
                        {errors.current_password && (
                          <span className="help-block text-danger">
                            {errors.current_password}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Password fields - matches Laravel conditional logic */}
                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="password"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Password
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        value={user.password}
                        onChange={handleChange}
                        className="form-control"
                        autoComplete="new-password"
                      />
                      <span className="help-block">
                        Leave empty if you don't want to change password
                      </span>
                    </div>
                  </div>

                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="confirm_password"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Password Confirmation
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="confirm_password"
                        name="confirm_password"
                        type="password"
                        value={user.confirm_password}
                        onChange={handleChange}
                        className="form-control"
                        autoComplete="new-password"
                      />
                      <span className="help-block">
                        Leave empty if you don't want to change password
                      </span>
                    </div>
                  </div>

                  {/* Photograph - using S3ImageUpload component */}
                  <div
                    className={`form-group mb-3${
                      errors.image ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="image"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Photograph
                    </label>
                    <div className="col-sm-5">
                      <S3ImageUpload
                        onImageUpload={handleImageUpload}
                        currentImage={user.image}
                        folder="uploads/images/users"
                        helpText="The photograph must be a clear headshot of true likeness of yourself. The file can be no larger than 100MB"
                        maxSize={100 * 1024 * 1024} // 100MB
                        accept="image/*"
                        showPreview={true}
                        previewClassName="img-responsive mb-20"
                        exampleImage="/images/photograph-woman.jpg"
                      />
                      {errors.image && (
                        <span className="help-block text-danger">
                          {errors.image}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="clearfix"></div>

                  {/* Job Title */}
                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="job_title"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Job Title
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="job_title"
                        name="job_title"
                        value={user.job_title}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="phone_number"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Phone Number
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="phone_number"
                        name="phone_number"
                        value={user.phone_number}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onInput={handleInput}
                        className="form-control"
                        placeholder="e.g., 020 7123 4567"
                        inputMode="tel"
                        pattern="[0-9\s\-+()]+"
                      />
                    </div>
                  </div>

                  {/* Mobile Number */}
                  <div
                    className={`form-group mb-3${
                      errors.mobile_number ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="mobile_number"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Mobile Number
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="mobile_number"
                        name="mobile_number"
                        value={user.mobile_number}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onInput={handleInput}
                        className="form-control"
                        placeholder="e.g., +44 7123 456789 or 07123456789"
                        inputMode="tel"
                        pattern="[0-9\s\-+()]+"
                        required
                      />
                      <div className="help-block" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Characters: {user.mobile_number.replace(/\D/g, '').length} digits
                        {user.mobile_number.replace(/\D/g, '').length < 10 && (
                          <span style={{ color: '#d9534f' }}> (minimum 10 digits required)</span>
                        )}
                        {user.mobile_number.replace(/\D/g, '').length > 15 && (
                          <span style={{ color: '#d9534f' }}> (maximum 15 digits allowed)</span>
                        )}
                        {user.mobile_number.replace(/\D/g, '').length >= 10 && user.mobile_number.replace(/\D/g, '').length <= 15 && (
                          <span style={{ color: '#5cb85c' }}> ✓</span>
                        )}
                      </div>
                      {errors.mobile_number && (
                        <span className="help-block text-danger">
                          {errors.mobile_number}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Address fields */}
                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="address_1"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Address 1
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="address_1"
                        name="address_1"
                        value={user.address_1}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="address_2"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Address 2
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="address_2"
                        name="address_2"
                        value={user.address_2}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="address_3"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Address 3
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="address_3"
                        name="address_3"
                        value={user.address_3}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="town"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Town
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="town"
                        name="town"
                        value={user.town}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group mb-3"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="postcode"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Postcode
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="postcode"
                        name="postcode"
                        value={user.postcode}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Fax Number - conditional for client/contractor */}
                  <div
                    className={`form-group mb-3${
                      isClient || isContractor ? "" : " d-none"
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                    }}
                  >
                    <label
                      htmlFor="fax_number"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Fax Number
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="fax_number"
                        name="fax_number"
                        value={user.fax_number}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Staff and Display Options - only visible when user has users.edit permission (matching Laravel @if(in_array('users.edit', $globalPermissions))) */}
                  {!isSelfEdit && (
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Staff
                      </label>
                      <div className="col-sm-5">
                        <label style={{ marginRight: 10 }}>
                          <input
                            type="radio"
                            name="staff"
                            value="0"
                            checked={user.staff === 0}
                            onChange={() => handleRadioChange("staff", 0)}
                            className="form-checkbox"
                          />{" "}
                          No
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="staff"
                            value="1"
                            checked={user.staff === 1}
                            onChange={() => handleRadioChange("staff", 1)}
                            className="form-checkbox"
                          />{" "}
                          Yes
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Staff-only fields - matches Laravel js-staff-fields logic */}
                  {isStaff && (
                    <>
                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Display on Page Guardian
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="staff_guardian_page"
                              value="0"
                              checked={user.staff_guardian_page === 0}
                              onChange={() =>
                                handleRadioChange("staff_guardian_page", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="staff_guardian_page"
                              value="1"
                              checked={user.staff_guardian_page === 1}
                              onChange={() =>
                                handleRadioChange("staff_guardian_page", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      {/* Staff-only: certificate review access */}
                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Can review certificates
                        </label>
                        <div className="col-sm-5">
                          <label>
                            <input
                              type="checkbox"
                              name="can_review_certificates"
                              checked={user.can_review_certificates === 1}
                              onChange={(e) =>
                                handleRadioChange(
                                  "can_review_certificates",
                                  e.target.checked ? 1 : 0
                                )
                              }
                              className="form-checkbox"
                            />{" "}
                            Allow this user to review certificates
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Display on Property Owners
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="staff_property_owners"
                              value="0"
                              checked={user.staff_property_owners === 0}
                              onChange={() =>
                                handleRadioChange("staff_property_owners", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="staff_property_owners"
                              value="1"
                              checked={user.staff_property_owners === 1}
                              onChange={() =>
                                handleRadioChange("staff_property_owners", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Display on About Us
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="staff_about_us_page"
                              value="0"
                              checked={user.staff_about_us_page === 0}
                              onChange={() =>
                                handleRadioChange("staff_about_us_page", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="staff_about_us_page"
                              value="1"
                              checked={user.staff_about_us_page === 1}
                              onChange={() =>
                                handleRadioChange("staff_about_us_page", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          htmlFor="department_id"
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Department
                        </label>
                        <div className="col-sm-5">
                          <select
                            id="department_id"
                            name="department_id"
                            value={user.department_id}
                            onChange={handleChange}
                            className="form-control"
                          >
                            <option value="">Please Select</option>
                            {dropdowns.departments.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          htmlFor="biography"
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Biography
                        </label>
                        <div className="col-sm-9">
                          <CKEditor
                            editor={ClassicEditor}
                            data={user.biography}
                            config={{
                            licenseKey: "GPL",
                              height: "200px",
                              toolbar: [
                                "heading",
                                "|",
                                "bold",
                                "italic",
                                "link",
                                "bulletedList",
                                "numberedList",
                                "|",
                                "outdent",
                                "indent",
                                "|",
                                "blockQuote",
                                "insertTable",
                                "undo",
                                "redo",
                              ],
                              placeholder: "Enter biography here...",
                              removePlugins: [
                                "CKFinderUploadAdapter",
                                "CKFinder",
                                "EasyImage",
                                "Image",
                                "ImageCaption",
                                "ImageStyle",
                                "ImageToolbar",
                                "ImageUpload",
                              ],
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setUser((prev) => ({ ...prev, biography: data }));
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Calendar Color - only visible for staff (matching Laravel permission check) */}
                  {isStaff && (
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="color"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Calendar Color
                      </label>
                      <div className="col-sm-5">
                        <div
                          className="input-group colorpicker-component"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <input
                            id="color"
                            name="color"
                            type="text"
                            value={user.color}
                            onChange={handleColorChange}
                            className="form-control"
                            style={{
                              width: "120px",
                              height: "38px",
                              marginRight: "10px",
                            }}
                            placeholder="#000000"
                          />
                          <input
                            type="color"
                            value={getValidColor(user.color)}
                            onChange={handleColorChange}
                            style={{
                              width: "40px",
                              height: "38px",
                              border: "1px solid #ccc",
                              borderRadius: "3px",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notification Options - only visible for staff (matching Laravel permission check) */}
                  {isStaff && (
                    <>
                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          className="control-label col-sm-4"
                          style={{ textAlign: "right" }}
                        >
                          Receive notification of property status
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="rec_notif_of_property_status"
                              value="0"
                              checked={user.rec_notif_of_property_status === 0}
                              onChange={() =>
                                handleRadioChange("rec_notif_of_property_status", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="rec_notif_of_property_status"
                              value="1"
                              checked={user.rec_notif_of_property_status === 1}
                              onChange={() =>
                                handleRadioChange("rec_notif_of_property_status", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group mb-3"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                        }}
                      >
                        <label
                          className="control-label col-sm-4"
                          style={{ textAlign: "right" }}
                        >
                          Receive notification of property ID
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="rec_notif_of_property_id"
                              value="0"
                              checked={user.rec_notif_of_property_id === 0}
                              onChange={() =>
                                handleRadioChange("rec_notif_of_property_id", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="rec_notif_of_property_id"
                              value="1"
                              checked={user.rec_notif_of_property_id === 1}
                              onChange={() =>
                                handleRadioChange("rec_notif_of_property_id", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Gender - always visible, required for all users */}
                  {isGuardian && (
                    <div
                      className={`form-group mb-3${
                        errors.gender ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="gender"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Gender
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="gender"
                          name="gender"
                          value={user.gender}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Please Select</option>
                          {dropdowns.genders.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                        {errors.gender && (
                          <span className="help-block text-danger">
                            {errors.gender}
                          </span>
                        )}
                      </div>
                                                {parseInt(user.gender) === 6 && (
                        <div className="col-sm-3">
                          <input
                            className={`form-control ${errors.gender_freetext ? 'has-error' : ''}`}
                            type="text"
                            id="gender_freetext"
                            name="gender_freetext"
                            value={user.gender_freetext}
                            onChange={handleChange}
                            placeholder="Please specify gender"
                            maxLength="25"
                            required
                          />
                          <div className="help-block">
                            Maximum 25 characters
                          </div>
                          {errors.gender_freetext && (
                            <span className="help-block text-danger">
                              {errors.gender_freetext}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </fieldset>

                {/* Guardian Fields - matches Laravel fields-guardian.blade.php */}
                {isGuardian && (
                  <>
                    {/* Nationality */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="country_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Nationality
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="country_id"
                          name="country_id"
                          value={user.country_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.nationalities.map((n) => (
                            <option key={n.id} value={n.id}>
                              {n.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Religion */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="religion_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Religion
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="religion_id"
                          name="religion_id"
                          value={user.religion_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.religions.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* How did you hear about Global Guardians? */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="hear_about_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        How did you hear about Global Guardians?
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="hear_about_id"
                          name="hear_about_id"
                          value={user.hear_about_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.hearAbouts.map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Date of Birth */}
                    <div
                      className={`form-group mb-3${
                        errors.date_of_birth ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="date_of_birth"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Date of Birth
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="date_of_birth"
                          name="date_of_birth"
                          type="date"
                          value={convertToISODate(user.date_of_birth)}
                          onChange={handleChange}
                          className="form-control"
                          max={new Date().toISOString().split('T')[0]}
                        />
                        {errors.date_of_birth && (
                          <span className="help-block text-danger">
                            {errors.date_of_birth}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* National Insurance Number */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="national_insurance_number"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        National Insurance Number
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="national_insurance_number"
                          name="national_insurance_number"
                          value={user.national_insurance_number}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Any Criminal Record? */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Any Criminal Record?</label>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "140px", marginLeft: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px" }}>
                          <input
                            type="radio"
                            name="criminal_record"
                            value={0}
                            checked={user.criminal_record === 0}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px", marginLeft: "10px" }}>
                          <input
                            type="radio"
                            name="criminal_record"
                            value={1}
                            checked={user.criminal_record === 1}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    {/* Criminal Record Description - only visible when Yes is selected */}
                    {user.criminal_record === 1 && (
                      <div className="form-group create-property-textarea-field">
                        <label className="control-label">Criminal Record Description</label>
                        <div>
                          <textarea
                            name="criminal_record_description"
                            value={user.criminal_record_description}
                            onChange={handleChange}
                            className="form-control"
                            rows={4}
                            placeholder="Please provide details of any criminal record..."
                          />
                          {errors.criminal_record_description && (
                            <span className="help-block text-danger">
                              {errors.criminal_record_description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Any Medical Condition? */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Any Medical Condition?</label>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "140px", marginLeft: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px" }}>
                          <input
                            type="radio"
                            name="medical_condition"
                            value={0}
                            checked={user.medical_condition === 0}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px", marginLeft: "10px" }}>
                          <input
                            type="radio"
                            name="medical_condition"
                            value={1}
                            checked={user.medical_condition === 1}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    {/* Medical Condition Description - only visible when Yes is selected */}
                    {user.medical_condition === 1 && (
                      <div className="form-group create-property-textarea-field">
                        <label className="control-label">Medical Condition Description</label>
                        <div>
                          <textarea
                            name="medical_condition_description"
                            value={user.medical_condition_description}
                            onChange={handleChange}
                            className="form-control"
                            rows={4}
                            placeholder="Please provide details of any medical condition..."
                          />
                          {errors.medical_condition_description && (
                            <span className="help-block text-danger">
                              {errors.medical_condition_description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Details of Next of Kin */}
                    <div className="form-group create-property-textarea-field">
                      <label className="control-label">Details of Next of Kin</label>
                      <div>
                        <textarea
                          name="next_of_kin"
                          value={user.next_of_kin}
                          onChange={handleChange}
                          className="form-control"
                          rows={4}
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="form-group create-property-textarea-field">
                      <label className="control-label">Notes</label>
                      <div>
                        <textarea
                          name="notes"
                          value={user.notes}
                          onChange={handleChange}
                          className="form-control"
                          rows={16}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Client Contact Fields - matches Laravel fields-client-contact.blade.php */}
                {isClient && (
                  <>
                    {/* Client - read-only when editing existing client contact (matching Laravel) */}
                    <div
                      className={`form-group mb-3${
                        errors.client_id ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="client_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Client
                      </label>
                      <div className="col-sm-5">
                        {user.client_id && parseInt(user.client_id) > 0 ? (
                          // Show client name as static text when editing (matching Laravel)
                          <div className="form-control-static" style={{ paddingTop: '7px' }}>
                            {dropdowns.clients.find((c) => c.id === parseInt(user.client_id))
                              ?.name || "Unknown Client"}
                          </div>
                        ) : (
                          // Show dropdown only for new client contacts
                          <select
                            id="client_id"
                            name="client_id"
                            value={user.client_id || ""}
                            onChange={handleChange}
                            className="form-control"
                          >
                            <option value="">Please Select</option>
                            {dropdowns.clients.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {errors.client_id && (
                          <span className="help-block text-danger">
                            {errors.client_id}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Position */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="position"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Position
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="position"
                          name="position"
                          value={user.position}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Department */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="department"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Department
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="department"
                          name="department"
                          value={user.department}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Reports To */}
                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="reports_to"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Reports To
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="reports_to"
                          name="reports_to"
                          value={user.reports_to}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* Description and Access to NTQ - only visible for staff/admin editing client contacts (matching Laravel @if($currentUser->isStaff() || in_array('users.edit', $globalPermissions))) */}
                    {!isSelfEdit && (
                      <>
                        {/* Description */}
                        <div
                          className="form-group mb-3"
                          style={{
                            flexDirection: "row",
                            alignContent: "center",
                            gap: 40,
                          }}
                        >
                          <label
                            htmlFor="description"
                            className="control-label col-sm-3"
                            style={{ textAlign: "right" }}
                          >
                            Description
                          </label>
                          <div className="col-sm-5">
                            <textarea
                              id="description"
                              name="description"
                              value={user.description}
                              onChange={handleChange}
                              className="form-control"
                            />
                          </div>
                        </div>
                        {/* Access to NTQ */}
                        <div
                          className="form-group mb-3"
                          style={{
                            flexDirection: "row",
                            alignContent: "center",
                            gap: 40,
                          }}
                        >
                          <label
                            className="control-label col-sm-3"
                            style={{ textAlign: "right" }}
                          >
                            Access To NTQ
                          </label>
                          <div className="col-sm-5">
                            <label style={{ marginRight: 10 }}>
                              <input
                                type="radio"
                                name="access_to_ntq"
                                value="0"
                                checked={user.access_to_ntq === 0}
                                onChange={() =>
                                  setUser((prev) => ({ ...prev, access_to_ntq: 0 }))
                                }
                                className="form-checkbox"
                              />{" "}
                              No
                            </label>
                            <label>
                              <input
                                type="radio"
                                name="access_to_ntq"
                                value="1"
                                checked={user.access_to_ntq === 1}
                                onChange={() =>
                                  setUser((prev) => ({ ...prev, access_to_ntq: 1 }))
                                }
                                className="form-checkbox"
                              />{" "}
                              Yes
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Contractor Contact Fields - matches Laravel fields-contractor-contact.blade.php */}
                {isContractor && (
                  <>
                    <div
                      className={`form-group mb-3${
                        errors.contractor_id ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="contractor_id"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Contractor
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="contractor_id"
                          name="contractor_id"
                          value={user.contractor_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.contractors.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.contractor_id && (
                          <span className="help-block text-danger">
                            {errors.contractor_id}
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="contractor_position"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Position
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="contractor_position"
                          name="contractor_position"
                          value={user.contractor_position}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="contractor_department"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Department
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="contractor_department"
                          name="contractor_department"
                          value={user.contractor_department}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="contractor_reports_to"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Reports To
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="contractor_reports_to"
                          name="contractor_reports_to"
                          value={user.contractor_reports_to}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div
                      className="form-group mb-3"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                      }}
                    >
                      <label
                        htmlFor="date_started_with_company"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Date Started With Company
                      </label>
                      <div className="col-sm-5">
                        <DatePicker
                          id="date_started_with_company"
                          selected={
                            user.date_started_with_company
                              ? parseDateUK(user.date_started_with_company) || null
                              : null
                          }
                          onChange={(date) => {
                            const formattedDate = date ? formatDateUK(date) : "";
                            handleChange({
                              target: {
                                name: "date_started_with_company",
                                value: formattedDate,
                              },
                            });
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="dd/mm/yyyy"
                          className={`form-control${errors.date_started_with_company ? " has-error" : ""}`}
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          isClearable
                          wrapperClassName="date-picker-wrapper"
                        />
                        {errors.date_started_with_company && (
                          <span className="help-block text-danger">
                            {errors.date_started_with_company}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <fieldset className="form-horizontal">
                  <div
                    className="form-group text-right"
                    style={{
                      marginTop: 32,
                      display: "flex",
                      alignSelf: "center",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <div className="col-sm-3"></div>
                    {/* <div className="col-sm-5"> */}
                    <div
                      style={{
                        display: "flex",
                        alignSelf: "center",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="submit"
                        className="btn btn-add-user"
                        style={{
                          minWidth: 120,
                          background: "#1B2046",
                          color: "white",
                        }}
                        disabled={saving}
                      >
                        <i className="glyphicon glyphicon-ok"></i>{" "}
                        {saving ? "Saving..." : (isSelfEdit ? "Save my profile" : "Save user")}
                      </button>
                      {success && (
                        <span className="text-success ml-2">Saved!</span>
                      )}
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEdit;
